<template>
  <v-dialog v-model="dialog" max-width="720" scrollable>
    <v-card :loading="isLoading">
      <v-card-title class="headline">
        {{ $t("STOCK.INVENTORY.LOT_BREAKDOWN_TITLE") }}
      </v-card-title>
      <v-card-subtitle v-if="productLabel" class="pb-0">
        {{ productLabel }}
        <span v-if="warehouseLabel" class="grey--text"> · {{ warehouseLabel }}</span>
        <span v-if="asOfDate" class="grey--text"> · {{ $t("STOCK.INVENTORY.AS_OF_DATE") }}: {{ asOfDate }}</span>
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>
        <v-simple-table v-else-if="lots.length" dense>
          <thead>
            <tr>
              <th>{{ $t("STOCK.RECEIPTS.LOT_NUMBER") }}</th>
              <th class="text-right">{{ $t("STOCK.INVENTORY.ON_HAND") }}</th>
              <th>{{ $t("STOCK.RECEIPTS.EXPIRY") }}</th>
              <th>{{ $t("STOCK.INVENTORY.PRODUCTION_DATE") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(lot, index) in lots" :key="`lot-${index}-${lot.lot_number}`">
              <td>{{ lot.lot_number || $t("STOCK.INVENTORY.NO_LOT") }}</td>
              <td class="text-right">
                {{ formatQty(lot.quantity_on_hand) }}
                <span v-if="unitCode" class="grey--text text--darken-1">{{ unitCode }}</span>
              </td>
              <td>{{ lot.expiry_date || "-" }}</td>
              <td>{{ lot.production_date || "-" }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th>{{ $t("STOCK.INVENTORY.LOT_TOTAL") }}</th>
              <th class="text-right">
                {{ formatQty(totalQty) }}
                <span v-if="unitCode" class="grey--text text--darken-1">{{ unitCode }}</span>
              </th>
              <th colspan="2"></th>
            </tr>
          </tfoot>
        </v-simple-table>
        <v-alert v-else-if="!isLoading" type="info" dense text>
          {{ $t("STOCK.INVENTORY.NO_LOTS") }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="dialog = false">
          {{ $t("GENERAL.LABELS.CLOSE") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { GET_STOCK_INVENTORY_LOT_BREAKDOWN } from "@/store/stock/stockInventory.module";

export default {
  name: "StockInventoryLotBreakdownDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    stockProductId: {
      type: Number,
      default: null,
    },
    asOfDate: {
      type: String,
      default: null,
    },
    productDisplayName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: null,
      lots: [],
      productLabel: "",
      warehouseLabel: "",
      unitCode: "",
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    totalQty() {
      return this.lots.reduce((sum, lot) => sum + Number(lot.quantity_on_hand || 0), 0);
    },
  },
  watch: {
    value(open) {
      if (open && this.stockProductId) {
        this.loadBreakdown();
      }
    },
  },
  methods: {
    formatQty(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    },
    loadBreakdown() {
      this.isLoading = true;
      this.errorMessage = null;

      const params = {
        stock_product_id: this.stockProductId,
      };

      if (this.asOfDate) {
        params.as_of_date = this.asOfDate;
      }

      return this.$store
        .dispatch(GET_STOCK_INVENTORY_LOT_BREAKDOWN, params)
        .then((data) => {
          this.lots = data.lots || [];
          this.productLabel =
            (data.product && (data.product.display_name || data.product.code)) ||
            this.productDisplayName;
          this.warehouseLabel = data.warehouse ? data.warehouse.name : "";
          this.unitCode =
            data.product && data.product.primary_unit ? data.product.primary_unit.code : "";
        })
        .catch((errors) => {
          this.lots = [];
          this.errorMessage =
            (errors && errors.message) ||
            (errors && errors.errors && Object.values(errors.errors).flat().join(" ")) ||
            this.$t("GENERAL.UNEXPECTED_ERROR");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
